import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DomSanitizer} from "@angular/platform-browser";
import {RouteService} from "../../services/routeService";
import {marked} from "marked";
import {Router} from "@angular/router";
import {DICTIONARY} from "../../dictionary";
import {GeneralService} from "../../services/generalService";
import {NotificationService} from "../../services/notificationService";
import DOMPurify from 'dompurify';

@Component({
    selector: 'ai-assistant-chat',
    templateUrl: './ai-assistant-chat.component.html',
    styleUrls: ['./ai-assistant-chat.component.scss']
})
export class AiAssistantChatComponent implements OnInit {

	@Input() isHeadless = false;

	@Output() closeCb = new EventEmitter();
	@ViewChild('chatMessagesContainer') chatMessagesContainer: ElementRef;
	@ViewChild('questionInput') questionInput: ElementRef;

	constructor(private sanitizer:DomSanitizer,
				private router:Router,
				public gs:GeneralService,
				public ns:NotificationService,
				private rs:RouteService) {
	}

	conversation = [];
	inProcess;
	chatInputTxt;
	isFromSupport = false;
	sessionId;
	showLoop;
	showTicketLabels;
	dic = DICTIONARY;

	// CC: starter prompts on the welcome screen - clicking one sends it as the first message
	suggestions = [
		{icon: 'far fa-shield-check', text: 'Why was an email quarantined?'},
		{icon: 'far fa-envelope', text: 'An email was not delivered'},
		{icon: 'far fa-lock', text: 'How do I send an encrypted email?'}
	];

	// CC: taken from email-viewer.component.ts -> ngOnChanges
	private toSafeHtml(html) {
		return this.sanitizer.bypassSecurityTrustHtml(DOMPurify.sanitize(html || ''));
	}

	ngOnInit(): void {
		this.showTicketLabels = this.router.url.includes(this.dic.CONSTANTS.supportPages.tickets);
		this.addGreetingMessage();
	}

	// CC: conversation[0] is always the bot greeting (history/export/ticket logic skip index 0); the welcome screen in the template renders it, so it is not shown as a bubble
	addGreetingMessage = () => {
		const initialMessage = 'Hi! I’m the Trustifi Support Assistant. Describe your issue in detail — I have access to your account logs, diagnostic tools, and our full knowledge base, so the more you share, the faster I can help.';

		this.conversation.push({
			role: 'assistant',
			message: initialMessage,
			sanitizedMessage: this.toSafeHtml(initialMessage),
			reference: null
		});
	}

	focusInput = () => {
		setTimeout(() => {
			this.questionInput?.nativeElement.focus();
		}, 10);
	}


	aiAssistantExecute = (question) => {
		if (!question?.trim()) {
			return;
		}

		if (this.chatMessagesContainer?.nativeElement) {
			setTimeout(() => {
				this.chatMessagesContainer.nativeElement.scrollTo({
					top: this.chatMessagesContainer.nativeElement.scrollHeight,
					behavior: 'smooth'
				});
			}, 10);
		}

		this.inProcess = true;

		const historyChat = this.conversation.map(message => ({
			role: message.role,
			content: message.message
		}));

		// remove bot's "how can i help you" message from history
		historyChat.shift();

		const actionInfo = {
			prompt: question,
			historyChat,
			sessionId: this.sessionId,
			isFromSupport: this.isFromSupport
		};

		this.conversation.push({
			role: 'user',
			message: question,
			sanitizedMessage: this.toSafeHtml(question),
		});

		this.chatInputTxt = '';

		this.rs.knowledgeMcpQuery(actionInfo).then(res => {
			if (!this.conversation.length || this.conversation[this.conversation.length - 1]?.message !== question) {
				return;
			}

			const html = marked.parse(res.answer) as string;
			this.sessionId = res.sessionId;
			this.conversation.push({
				role: 'assistant',
				message: html,
				sanitizedMessage: this.toSafeHtml(html),
				reference: res.reference
			});
			this.inProcess = false;
			this.focusInput();
		}, err => {
			this.inProcess = false;
			this.focusInput();
		});
	}

	resetConversation = () => {
		this.conversation = [];
		this.sessionId = null;
		// CC: a reset while a reply is pending makes the pending handler bail out before clearing inProcess, which left the input disabled
		this.inProcess = false;
		this.addGreetingMessage();
		this.focusInput();
	}

	goToTicketsPage = () => {
		if (this.isFromSupport) {
			return;
		}

		this.router.navigateByUrl('/dummy', { skipLocationChange: true }).then(() => {
			this.router.navigate([this.dic.CONSTANTS.appStates.accountDetails, this.dic.CONSTANTS.supportPages.tickets], {state: {data: {aiConversation: this.conversation || null}}});
		});
	}

	copyMessageToClipboard = (message) => {
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = message;
		this.gs.copyToClipboard(tempDiv.innerText);
	}

	likeMessage = (messageObj, isPositiveLike) => {
		if (isPositiveLike) {
			messageObj.likeState =
				messageObj.likeState === 'positive' ? 'neutral' : 'positive';
		}
		else {
			messageObj.likeState =
				messageObj.likeState === 'negative' ? 'neutral' : 'negative';
		}

		if (messageObj.likeState === 'neutral') {
			return;
		}

		const actionInfo = {
			action: 'like',
			state: messageObj.likeState,
			sessionId: this.sessionId
		};

		this.rs.updateKnowledgeBaseQuery(actionInfo).then(res => {});
	}

	exportConversation = () => {
		const tempDiv = document.createElement('div');
		const lines = this.conversation.slice(1).map(item => {
			tempDiv.innerHTML = item.message;
			const text = tempDiv.innerText;
			const role = item.role === 'user' ? 'You' : 'Trustifi AI';
			return `${role}:\n${text}`;
		});

		const date = new Date().toLocaleDateString('en-US', {
			year: 'numeric', month: 'long', day: 'numeric'
		});
		const content = `Chat with Trustifi AI — Exported ${date}\n\n` + lines.join('\n\n---\n\n');
		this.gs.exportCsv(content, 'trustifi-ai-chat.txt');
	}

	openLinksInNewTab(e: MouseEvent): void {
		const a = (e.target as HTMLElement)?.closest('a') as HTMLAnchorElement;
		if (!a?.href) {
			return;
		}

		// ctrl / cmd / middle click
		if (e.ctrlKey || e.metaKey || e.button !== 0) return;

		e.preventDefault();
		window.open(a.href, '_blank', 'noopener,noreferrer');
	}
}
